/**
 * The backend's muscle-group taxonomy, and the parser for its PascalCase enum names.
 *
 * Ported from `move-with-me-frontend/.../features/exercises/model/muscleGroup.ts`, with one thing
 * deliberately left behind: mobile's enum values double as i18n keys into
 * `exerciseLibrary.muscles.*`, and that mapping is kept, but there is **no colour map here**. On
 * the phone a muscle group's colour is data in the model layer; here it is a `--mg-*` custom
 * property in the token layer, because the display palette has six buckets and this taxonomy has
 * fourteen — the two are not the same list and pairing them in one table would imply they are.
 *
 * The rollup from these fourteen to the six display groups lives in `entities/plan-draft`'s
 * `muscleVolume`, keyed on the wire's own strings. That is not duplication: this module answers
 * "what does the backend call this?", and that one answers "which bar does it land in?".
 */

/**
 * Every muscle group the backend names. The values are lower-cased slugs that double as i18n keys —
 * they line up one-to-one with the upstream `exerciseLibrary.muscles.*` group, so muscle labels
 * reuse copy that already exists and need no new strings.
 *
 * A union of literals rather than a TypeScript `enum`: `erasableSyntaxOnly` is on in
 * `tsconfig.json`, and an `enum` emits a runtime object, so it is not erasable syntax. The union
 * plus the table below gives the same exhaustiveness with none of the emit.
 */
export type MuscleGroup =
  | 'chest'
  | 'back'
  | 'shoulders'
  | 'biceps'
  | 'triceps'
  | 'forearms'
  | 'abs'
  | 'obliques'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'calves'
  | 'fullBody'
  | 'other';

/**
 * Lower-cased backend name → the app's slug.
 *
 * Lookups are case-insensitive, which is why the composite name needs its own entry: `"fullbody"`
 * does not equal the `fullBody` slug through a naive lower-case round trip, and a group that
 * silently failed to parse would drop a full-body movement's entire contribution.
 */
const FROM_API: Readonly<Record<string, MuscleGroup>> = {
  chest: 'chest',
  back: 'back',
  shoulders: 'shoulders',
  biceps: 'biceps',
  triceps: 'triceps',
  forearms: 'forearms',
  abs: 'abs',
  obliques: 'obliques',
  quads: 'quads',
  hamstrings: 'hamstrings',
  glutes: 'glutes',
  calves: 'calves',
  fullbody: 'fullBody',
  other: 'other',
};

/**
 * The app's slug → the backend's PascalCase name. The inverse of the table above, kept explicit
 * for every value rather than derived, because the composite name (`fullBody` → `FullBody`) is
 * exactly what a naive capitalise gets wrong.
 *
 * Exhaustive over the union, so widening `MuscleGroup` without teaching this is a compile error.
 */
const TO_API: Record<MuscleGroup, string> = {
  chest: 'Chest',
  back: 'Back',
  shoulders: 'Shoulders',
  biceps: 'Biceps',
  triceps: 'Triceps',
  forearms: 'Forearms',
  abs: 'Abs',
  obliques: 'Obliques',
  quads: 'Quads',
  hamstrings: 'Hamstrings',
  glutes: 'Glutes',
  calves: 'Calves',
  fullBody: 'FullBody',
  other: 'Other',
};

/** Every group this build knows. */
export const ALL_MUSCLE_GROUPS: readonly MuscleGroup[] = Object.keys(TO_API) as MuscleGroup[];

/** One backend name → one slug, or `null` when this build has never heard of it. */
export function muscleGroupFromApi(value: string | null | undefined): MuscleGroup | null {
  if (!value) return null;
  const key = value.trim().toLowerCase();
  // Guarded with `Object.hasOwn` rather than indexed and null-coalesced: a bare index reaches the
  // prototype chain, so `"constructor"` off the wire returns a FUNCTION — which is not nullish, so
  // `?? null` never fires and it is returned as if it were a muscle group.
  return Object.hasOwn(FROM_API, key) ? (FROM_API[key] ?? null) : null;
}

/** A list of backend names → slugs. Unrecognised values are dropped; order is preserved. */
export function muscleGroupsFromApi(
  values: readonly (string | null | undefined)[] | null | undefined,
): MuscleGroup[] {
  return (values ?? [])
    .map(muscleGroupFromApi)
    .filter((group): group is MuscleGroup => group !== null);
}

/** One slug → the backend's PascalCase name, for a request that has to name a group. */
export function muscleGroupToApi(group: MuscleGroup): string {
  return TO_API[group];
}

/**
 * The i18n key for a group's display label. Reuses the upstream `exerciseLibrary.muscles.*` group,
 * which the locale copy already carries in both languages — reading upstream keys is exactly what a
 * verbatim copy is for.
 */
export function muscleGroupLabelKey(group: MuscleGroup): string {
  return `exerciseLibrary.muscles.${group}`;
}

/** One of the four headings the muscle groups are listed under. */
export type MuscleRegionKey = 'upper' | 'lower' | 'core' | 'other';

/**
 * Which region a muscle group is listed under, transcribed from the 17 September 2026 specimen's
 * `REGION` map — the library's filter panel and the creator's chip grid both draw it.
 *
 * A presentation grouping and nothing more: nothing narrows, counts or matches by it. **`other` is
 * the REMAINDER**, not a fourth list — three named regions plus everything the taxonomy holds that
 * they do not name, so a group the backend adds has a home the day it arrives.
 */
const NAMED_REGIONS: readonly {
  readonly key: MuscleRegionKey;
  readonly groups: readonly MuscleGroup[];
}[] = [
  { key: 'upper', groups: ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'forearms'] },
  { key: 'lower', groups: ['quads', 'hamstrings', 'glutes', 'calves'] },
  { key: 'core', groups: ['abs', 'obliques'] },
];

const NAMED = new Set(NAMED_REGIONS.flatMap((region) => region.groups));

export const MUSCLE_REGIONS: readonly {
  readonly key: MuscleRegionKey;
  readonly groups: readonly MuscleGroup[];
}[] = [
  ...NAMED_REGIONS,
  { key: 'other', groups: ALL_MUSCLE_GROUPS.filter((group) => !NAMED.has(group)) },
];
